import type { Id } from "@repo/convex/dataModel";
import { TEAMS_SPECIFIC_QUESTION_MAX_LENGTH } from "@repo/shared/teams-topic";
import { Send } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useStartTeamsConsultation } from "@/features/clarification/hooks";

export function TeamsConsultationDialog({
  clarificationRequestId,
  open,
  onOpenChange,
}: {
  clarificationRequestId: Id<"clarificationRequests">;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const startConsultation = useStartTeamsConsultation();
  const [invitees, setInvitees] = useState("");
  const [specificQuestion, setSpecificQuestion] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    const inviteeEmails = invitees
      .split(/[,\n]/)
      .map((value) => value.trim())
      .filter(Boolean);
    if (inviteeEmails.length === 0) {
      toast.error("Add at least one Teams invitee.");
      return;
    }
    setSubmitting(true);
    try {
      await startConsultation({
        clarificationRequestId,
        inviteeEmails,
        requesterNote: specificQuestion.trim() || undefined,
      });
      toast.success("Teams consultation started");
      onOpenChange(false);
    } catch {
      toast.error("Failed to start Teams consultation");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Invite via Teams</DialogTitle>
          <DialogDescription>
            Claudia names the Teams chat and asks your invitees. You will be included by default.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="teams-invitees">Invitees</Label>
            <Textarea
              id="teams-invitees"
              value={invitees}
              onChange={(event) => setInvitees(event.target.value)}
              placeholder="name@example.com, teammate@example.com"
            />
            <p className="text-xs text-muted-foreground">
              Add the people Claudia should ask. Your account is added automatically.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="teams-question">Specific question (optional)</Label>
            <Textarea
              id="teams-question"
              value={specificQuestion}
              onChange={(event) => setSpecificQuestion(event.target.value)}
              maxLength={TEAMS_SPECIFIC_QUESTION_MAX_LENGTH}
              placeholder="Add anything specific you want Claudia to ask."
            />
            <p className="text-xs text-muted-foreground">
              Leave blank to let Claudia infer the ask from the clarification thread.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={handleSubmit} disabled={submitting}>
            <Send className="size-4" />
            Start
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
